import { useMemo, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import useModal from '../../hooks/useModal';
import { formatPrice } from '../../utils/utils';
import FederalTax from '../widgets/retireOnlineStepper/RetireOnlineStep7/FederalTax';
import StateTax from '../widgets/retireOnlineStepper/RetireOnlineStep7/StateTax';

const federalTaxData = {
  step1: { filingStatus: undefined },
  step2: { taxablePay: undefined, annuallyPay: undefined },
  step3: {
    children: undefined,
    dependepts: undefined,
    additionalAmount: undefined,
    totalAmount: undefined,
  },
  step4: {
    otherIncome: undefined,
    deductions: undefined,
    extraWithholdings: undefined,
  },
};

const stateTaxData = {
  taxOption: undefined,
  state: { value: undefined, label: undefined },
  martialStatus: { value: undefined, label: undefined },
  numberOfAllowances: undefined,
  additionalAmount: undefined,
};

const TaxWithholding = () => {
  const [showStateTax, handleShowStateTax, handleCloseStateTax] = useModal();
  const [showFederalTax, handleShowFederalTax, handleCloseFederalTax] =
    useModal();

  const [federalTaxDataStorage, setFederalTaxDataStorage] =
    useState(federalTaxData);

  const [stateTaxDataStorage, setStateTaxDataStorage] = useState(stateTaxData);

  const handleSetFederalTaxData = (data: any) => {
    setFederalTaxDataStorage(data);
  };

  const handleSetStateData = (data: any) => {
    setStateTaxDataStorage(data);
  };

  const calculateTotalAmountStep3 = useMemo(() => {
    const { children, dependepts, additionalAmount } =
      federalTaxDataStorage.step3;

    let totalSum = 0;

    if (additionalAmount) {
      totalSum += additionalAmount;
    }

    if (children) {
      totalSum += children * 2000;
    }

    if (dependepts) {
      totalSum += dependepts * 500;
    }

    return totalSum;
  }, [federalTaxDataStorage.step3]);

  return (
    <Container className="cnd-custom-container px-7 px-lg-0 pt-lg-12 mt-11 mt-lg-12">
      <section style={{ maxWidth: 1070 }}>
        <h4>Tax Withholding</h4>
      </section>
      <section
        className="mt-10 bg-white rounded-1 p-10"
        style={{ maxWidth: 1070 }}
      >
        <Row className="m-0">
          <Col className="p-0 d-flex align-items-center">
            <h6 className="m-0 lh-xs text-sm-16-lg-20">
              Federal Tax Withholding
            </h6>
          </Col>
          <Col className="p-0 d-flex justify-content-end">
            <Button variant="primary" size="sm" onClick={handleShowFederalTax}>
              Change
            </Button>
            <FederalTax
              show={showFederalTax}
              handleClose={handleCloseFederalTax}
              data={federalTaxDataStorage}
              collectData={handleSetFederalTaxData}
            />
          </Col>
        </Row>
        <Row className="m-0 pt-3 border-bottom">
          <Col className="p-0 py-3 col-lg-2">
            <span className="text-caption fw-normal lh-xs">Plan</span>
          </Col>
          <Col className="p-0 py-3 ps-10">
            <span className="text-caption fw-normal lh-xs">
              How much you’re withholding per check
            </span>
          </Col>
        </Row>
        <Row className="m-0 pt-3">
          <Col className="p-0 py-3 col-lg-2">
            <span className="text-caption fw-normal lh-xs">
              Company Pension Plan
            </span>
          </Col>
          <Col className="p-0 py-3 ps-10">
            <span className="text-caption fw-normal lh-xs">
              {`Calculate based on Head of Household, ${formatPrice(
                calculateTotalAmountStep3
              )} Dependent Credits,
              ${formatPrice(
                federalTaxDataStorage.step4.otherIncome || 0
              )} Other Income, ${formatPrice(
                federalTaxDataStorage.step4.deductions || 0
              )} Deductions, and ${formatPrice(
                federalTaxDataStorage.step4.extraWithholdings || 0
              )} Extra Withholding.`}
            </span>
          </Col>
        </Row>
      </section>
      <section
        className="mt-10 bg-white rounded-1 p-10"
        style={{ maxWidth: 1070 }}
      >
        <Row className="m-0">
          <Col className="p-0 d-flex align-items-center">
            <h6 className="m-0 lh-xs text-sm-16-lg-20">
              State Tax Withholding
            </h6>
          </Col>
          <Col className="p-0 d-flex justify-content-end">
            <Button variant="primary" size="sm" onClick={handleShowStateTax}>
              Change
            </Button>
            <StateTax
              show={showStateTax}
              handleClose={handleCloseStateTax}
              data={stateTaxDataStorage}
              collectData={handleSetStateData}
            />
          </Col>
        </Row>
        <Row className="m-0 pt-3 border-bottom">
          <Col className="p-0 py-3 col-lg-2">
            <span className="text-caption fw-normal lh-xs">Plan</span>
          </Col>
          <Col className="p-0 py-3 ps-10 col-lg-2">
            <span className="text-caption fw-normal lh-xs">State</span>
          </Col>
          <Col className="p-0 py-3">
            <span className="text-caption fw-normal lh-xs">
              How much you’re withholding per check
            </span>
          </Col>
        </Row>
        <Row className="m-0 pt-3">
          <Col className="p-0 py-3 col-lg-2">
            <span className="text-caption fw-normal lh-xs">
              Company Pension Plan
            </span>
          </Col>
          <Col className="p-0 py-3 ps-10 col-lg-2">
            <span className="text-caption fw-normal lh-xs">
              {stateTaxDataStorage.state.label || 'VA'}
            </span>
          </Col>
          <Col className="p-0 py-3">
            <span className="text-caption fw-normal lh-xs">
              {`Calculate based on ${
                stateTaxDataStorage.martialStatus.label || 'Single'
              } with ${
                stateTaxDataStorage.numberOfAllowances || '0'
              } allowances plus an additional
              ${
                stateTaxDataStorage.additionalAmount
                  ? formatPrice(stateTaxDataStorage.additionalAmount)
                  : '0'
              }.`}
            </span>
          </Col>
        </Row>
      </section>
    </Container>
  );
};

export default TaxWithholding;
